import type { BrandArtDto } from '@nsb/core';
import { useQuery } from '@tanstack/react-query';
import type { ReactNode } from 'react';
import { api } from '../api.ts';
import { FrameElevation } from './drawings.tsx';

/**
 * A threshold of the portal, the sign-in page and the Allow page: no header, the words in the 42 and the atmosphere
 * running to the screen's edge in the 58 on a wide screen, a short band above the words on a phone (style guide v2 §12).
 * The art is the sign-in page's, a drawing until the generated art exists.
 */
export function Threshold({ children }: { children: ReactNode }) {
  const { data: brand } = useQuery({ queryKey: ['art', 'brand'], queryFn: () => api<BrandArtDto>('/art/brand'), staleTime: 3_600_000 });
  const art = brand?.login ?? null;
  return (
    // The whole width: the form in its 42, the atmosphere running to the screen's edge in the 58 (style guide v2 §12).
    <div className="grid w-full lg:min-h-dvh lg:grid-cols-[42fr_58fr]">
      <div className="px-(--gutter) pb-24 pt-12 sm:pt-20 lg:px-16">
        {art ? (
          // Phone: a short band above the form (style guide v2 §12).
          <div className="-mt-4 mb-10 aspect-[3/1] overflow-hidden rounded-[2px] bg-inset lg:hidden">
            <img src={`${art.url}?w=1024`} alt="" className="h-full w-full object-cover" style={{ objectPosition: 'center 40%' }} />
          </div>
        ) : null}
        <div className="max-w-sm lg:mx-auto">{children}</div>
      </div>
      {/* Atmosphere at the threshold (style guide v2 §12): a drawing until the generated art exists. */}
      <div className="relative hidden overflow-hidden border-l border-rule bg-inset lg:block">
        {art ? (
          <img
            src={`${art.url}?w=1600`}
            srcSet={art.widths.map((w) => `${art.url}?w=${w} ${w}w`).join(', ')}
            sizes="58vw"
            alt={art.alt}
            className="absolute inset-0 h-full w-full object-cover"
          />
        ) : (
          <FrameElevation className="absolute inset-x-[8%] bottom-[10%] w-[84%]" />
        )}
      </div>
    </div>
  );
}
